// Gifts Review (approved HTML: 07-Gifts-Review-v2-Clean.html).
//
// Priority focus, identity row, customer tabs (Gifts active), gift
// catalog (shirts / caps / half gallons / full gallons), gift delivery
// (add to next shipment vs ship now), gift history, gift review summary
// (customer agreement + attach button), eligibility checks, follow-up
// ownership card, next-customer preview, Finish and Next footer.
//
// Mirrors the eligibility logic in
// dist/account-workflows.giftEligibility (confirmed in
// dist/account-workflows.mjs): service gate must allow, no pending gift
// already, 90 days since last shipped gift. Catalog mirrors the
// GIFT_CATALOG items sample-gutx-half, sample-hydra-trial, sample-shirt,
// sample-cap.
//
// TODO(runtime): wire actions to
//   - services/account-actions GIFT request handler (proposed - TODO
//     runtime; dist/account-workflows.requestGift is confirmed but no
//     HTTP route lands yet)
//   - services/account-actions date change handler for pending-op guards
// R9 companion-state wiring: the gift-grid is wrapped in DataBoundary.
// External signals arrive via `companionSignals` so the runtime wires
// the real services/account-actions gift request handler and
// giftEligibility outcomes without this file branching on their
// shapes. The Default state visual is unchanged.
//
// W32 (2026-10-20) real-contact wiring: on a real HubSpot session
// (useReviewIdentity().isPreview === false) the screen renders the real
// identity and an honest "Nothing on file yet" body. The portal has no
// gift properties and the gift-eligibility read returns 503 (MEASURED
// 2026-10-20; W37 wires it), so the sample catalog, shipments, history
// and eligibility checks are not shown. The gift actions stay visible
// but inert ("Paused by the live-write gate"). The GIFT_CATALOG /
// UPCOMING_SHIPMENTS / SARAH / MICHAEL fixture is reachable ONLY in
// preview.
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { PriorityFocus } from '../_shared/PriorityFocus.js';
import { IdentityRow } from '../_shared/IdentityRow.js';
import { CustomerTabs } from '../_shared/CustomerTabs.js';
import { FileShortcuts } from '../_shared/FileShortcuts.js';
import { UpNext } from '../_shared/UpNext.js';
import { ActionFooter } from '../_shared/ActionFooter.js';
import { DataBoundary, type BoundaryState } from '../_shared/DataBoundary.js';
import { useReviewIdentity } from '../_shared/useReviewIdentity.js';
import {
  resolveCompanionState,
  type ScreenCompanionSignals,
} from '../_shared/useScreenCompanionState.js';
import { useActiveCallSignal, mergeMidCallSignal } from '../_shared/useActiveCallSignal.js';
import { GiftCatalog } from './components/GiftCatalog.js';
import { GiftDelivery, type DeliveryMode } from './components/GiftDelivery.js';
import { GiftHistory } from './components/GiftHistory.js';
import { GiftReview } from './components/GiftReview.js';
import { GiftEligibility } from './components/GiftEligibility.js';
import { GiftFollowup } from './components/GiftFollowup.js';
import {
  GIFT_CATALOG_BY_CATEGORY,
  UPCOMING_SHIPMENTS,
  type GiftCategoryKey,
  type GiftVariant,
} from './mockData.js';
import { SARAH } from '../../data/mockData.js';
import { useScreenRuntime } from '../CurrentActionReview/runtime-clients.js';
import { RuntimeApiError } from '../../api/runtimeClient.js';
import { LIVE_WRITE_PAUSED_LABEL } from '../_w2a_lower/liveWrite.js';
import { LiveGiftCatalog } from './components/LiveGiftCatalog.js';
import { LiveGiftEligibility } from './components/LiveGiftEligibility.js';
import { LiveGiftHistory } from './components/LiveGiftHistory.js';
import { LiveGiftReview } from './components/LiveGiftReview.js';
import {
  catalogByCategory,
  eligibilityPill,
  eligibilitySummary,
  mapGiftEligibilityBody,
  type GiftCategoryCode,
  type GiftEligibilityView,
} from './liveModel.js';
import './styles.css';
import './app.css';

export const GIFTS_CONTACT_NOT_FOUND_TITLE = 'Contact not found';

export interface GiftsReviewScreenProps {
  /** R9 test seam for the six companion states. */
  companionSignals?: ScreenCompanionSignals;
}

export function GiftsReviewScreen(
  props: GiftsReviewScreenProps = {},
): ReactNode {
  const params = useParams();
  const customerId = params.customerId || SARAH.id;
  const identity = useReviewIdentity(customerId);
  const isPreview = identity.isPreview;
  const firstName = identity.firstName;

  const [category, setCategory] = useState<GiftCategoryKey>('shirts');
  const [selected, setSelected] = useState<GiftVariant>(
    GIFT_CATALOG_BY_CATEGORY.shirts[0],
  );
  const [size, setSize] = useState('L');
  const [delivery, setDelivery] = useState<DeliveryMode>('next');
  const [selectedShipmentId, setSelectedShipmentId] = useState(
    UPCOMING_SHIPMENTS[0].id,
  );
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [resultMessage, setResultMessage] = useState<string | null>(null);

  // W47b (2026-10-21): the real-session read of GET /v1/contacts/:id/
  // gift-eligibility (W47 api-server) and the rep's local pick on it. Reset
  // whenever the contact changes.
  const [live, setLive] = useState<GiftEligibilityView | null>(null);
  const [liveCategory, setLiveCategory] = useState<GiftCategoryCode>('t_shirt');
  const [liveSelectedId, setLiveSelectedId] = useState<string | null>(null);
  const [liveSize, setLiveSize] = useState<string | null>(null);
  const [liveAgreed, setLiveAgreed] = useState(false);
  useEffect(() => {
    setLive(null);
    setLiveCategory('t_shirt');
    setLiveSelectedId(null);
    setLiveSize(null);
    setLiveAgreed(false);
  }, [customerId]);
  const liveItems = live ? catalogByCategory(live.catalog, liveCategory) : [];
  const liveSelected = (liveSelectedId ? live?.catalog.find((item) => item.id === liveSelectedId) : undefined) ?? liveItems[0] ?? null;

  const isShirt = selected.kind === 'T-SHIRT';
  const selectedShipment =
    UPCOMING_SHIPMENTS.find((s) => s.id === selectedShipmentId) ?? UPCOMING_SHIPMENTS[0];

  const shipmentSummary =
    delivery === 'now' ? 'Ship now · Barn address' : selectedShipment.summary;
  const variantSummary = isShirt ? `${selected.variant} · ${size}` : selected.variant;
  const shippingSummary = delivery === 'now' ? 'Pending quote' : 'With selected order';
  const agreementCopy =
    delivery === 'now'
      ? `${SARAH.firstName} agreed to this gift as a separate shipment.`
      : `${SARAH.firstName} agreed to this gift and the selected shipment.`;
  const attachLabel = delivery === 'now' ? 'Ship now' : 'Add to next shipment';
  const reviewNote =
    delivery === 'now'
      ? 'Preview only. Live submission requires a verified address and approved shipping quote. Ship now requests fulfillment; it does not confirm dispatch.'
      : 'Confirm attachment before telling Sarah it is on the order. Attached is not the same as shipped.';

  const canSubmit = useMemo(() => {
    if (!agreed) return false;
    if (delivery === 'now' && !addressConfirmed) return false;
    if (!selected.available) return false;
    return true;
  }, [agreed, delivery, addressConfirmed, selected]);

  const handleCategoryChange = (key: GiftCategoryKey) => {
    setCategory(key);
    setSelected(GIFT_CATALOG_BY_CATEGORY[key][0]);
    resetSelection();
  };

  const handleSelect = (variant: GiftVariant) => {
    setSelected(variant);
    resetSelection();
  };

  function resetSelection() {
    setAgreed(false);
    setResultMessage(null);
  }

  const handleModeChange = (mode: DeliveryMode) => {
    setDelivery(mode);
    setAddressConfirmed(false);
    resetSelection();
  };

  const handleAttach = () => {
    setResultMessage(
      delivery === 'now'
        ? 'Preview only. No shipment was created. The live flow must review shipping cost before submission.'
        : 'Preview only. No gift has been attached to a real order.',
    );
  };

  // R10: fold the live Kixie mid-call signal into companion state. The
  // test seam (companionSignals.midCall) still wins so unit tests stay
  // hermetic.
  const liveMidCall = useActiveCallSignal({
    customerId,
    label: `Call in progress with ${isPreview ? SARAH.firstName : firstName}`,
    actionName: 'KIXIE_CALL_IN_PROGRESS',
  });

  // W2A upper lane: when the signed session is live, probe the gift
  // eligibility route (mirrors dist/account-workflows.giftEligibility).
  // Preview-fixture mode leaves this null so the illustrative catalog
  // stays rendered.
  const runtime = useScreenRuntime();
  const [fetchState, setFetchState] = useState<BoundaryState | null>(null);
  useEffect(() => {
    if (!runtime) { setFetchState(null); return; }
    let cancelled = false;
    setFetchState({ kind: 'loading', label: 'Loading gift options…' });
    runtime.signed
      .request<unknown>({
        method: 'GET',
        path: `/v1/contacts/${encodeURIComponent(customerId)}/gift-eligibility`,
      })
      .then((body) => {
        if (cancelled) return;
        // W47b: `eligible` false is no longer a blocked boundary; the
        // approved eligibility block renders the failed check and the
        // catalog stays visible with its inert "Add to next shipment".
        setLive(mapGiftEligibilityBody(body));
        setFetchState(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof RuntimeApiError && err.kind === 'NOT_FOUND') {
          setLive(null);
          if (err.errorCode === 'CONTACT_NOT_FOUND') {
            // W47: the route answers 404 CONTACT_NOT_FOUND when HubSpot has
            // no such contact (READ routes/stubs-w3c.mjs).
            setFetchState({
              kind: 'empty',
              title: GIFTS_CONTACT_NOT_FOUND_TITLE,
              body: `HubSpot has no contact with id ${customerId}. Open the Sales Workspace from a contact record.`,
            });
            return;
          }
          // Gift eligibility route not yet wired. Fall back to default
          // layout rather than reporting a false error.
          setFetchState(null);
          return;
        }
        if (err instanceof RuntimeApiError && (err.kind === 'AUTH_FAILED' || err.kind === 'FORBIDDEN')) {
          setFetchState({
            kind: 'blocked',
            title: 'Gift request restricted',
            reason: 'This rep is not authorized to send gifts for this contact.',
            gateName: err.errorCode,
          });
          return;
        }
        if (err instanceof RuntimeApiError && err.status === 503) {
          // W37b (2026-10-20): MEASURED 503 ROUTE_DEPENDENCY_NOT_WIRED
          // (giftEligibilitySink is not composed; gift-preparation needs
          // sources that are not on the api-server). Say that, not
          // "could not load".
          setFetchState({
            kind: 'error',
            title: 'Gift eligibility is not connected yet',
            detail: 'The gift eligibility read has no composed source on this deployment.',
            errorCode: err.errorCode ?? 'ROUTE_DEPENDENCY_NOT_WIRED',
          });
          return;
        }
        setFetchState({
          kind: 'error',
          title: 'Gift catalog could not load',
          detail: err instanceof Error ? err.message : 'Unknown error',
          errorCode: err instanceof RuntimeApiError ? err.errorCode : 'UNKNOWN',
        });
      });
    return () => { cancelled = true; };
  }, [runtime, customerId]);

  const mergedSignals = useMemo(
    () => mergeMidCallSignal(props.companionSignals, liveMidCall),
    [props.companionSignals, liveMidCall],
  );
  const state = useMemo<BoundaryState>(() => {
    const companion = resolveCompanionState(mergedSignals);
    if (companion.kind !== 'default') return companion;
    if (fetchState) return fetchState;
    return { kind: 'default' };
  }, [mergedSignals, fetchState]);

  if (!isPreview) {
    // W32: real session. Honest body, inert gift actions, no fixture.
    // W47b: with the W47 eligibility read the approved catalog / review /
    // eligibility / history layout renders from the body; without it (route
    // not wired) the W32 "Nothing on file yet" card stays.
    const ownerFirst = identity.owner.fullName === 'Unassigned' ? null : identity.owner.fullName.split(/\s+/)[0] ?? null;
    return (
      <WorkspaceChrome screen="gifts" rail="my-work">
        <div className="breadcrumbs">
          <div>
            My work
            <span className="slash">/</span>
            <strong>{identity.customer.fullName}</strong>
          </div>
          {identity.statusPill ? <span className="preview">{identity.statusPill}</span> : null}
        </div>
        <PriorityFocus
          ariaLabel="GIFTS · CUSTOMER APPRECIATION"
          eyebrow="GIFTS · CUSTOMER APPRECIATION"
          heading={`Choose a gift for ${firstName}`}
          what={{ label: 'WHAT', heading: live ? `Choose ${firstName}’s appreciation gift` : 'No gift history on file yet', iconId: 'message' }}
          whyNow={{
            label: 'WHY NOW',
            body: live ? eligibilitySummary(live) : 'No gift history or eligibility has been read into this workspace for this contact.',
            iconId: 'clock',
          }}
          nextAction={{
            label: 'NEXT ACTION',
            body: live
              ? 'Choose the gift and size; adding it to a shipment stays paused until the write gate opens.'
              : 'If a gift is warranted, note it in HubSpot; gift requests stay paused until the write gate opens.',
            detail: 'Confirm the choice before attaching the gift.',
            iconId: 'target',
          }}
          ownership={{ label: 'OWNERSHIP', iconId: 'people', detail: identity.ownerDetail }}
          owner={identity.owner}
        />
        <IdentityRow customer={identity.customer} badgeLabel={identity.badgeLabel} sinceLabel={identity.sinceLabel} />
        <FileShortcuts customerId={customerId} firstName={identity.customer.firstName} />
        <CustomerTabs customerId={customerId} active="gifts" />

        <DataBoundary state={state} testId="gifts-boundary">
          {live ? (
            <div className="gift-grid" data-testid="gifts-live-grid">
              <div className="gift-left">
                <LiveGiftCatalog
                  catalog={live.catalog}
                  category={liveCategory}
                  selectedId={liveSelected?.id ?? null}
                  statusLabel={eligibilityPill(live)}
                  eligible={live.eligible}
                  onCategoryChange={(key) => { setLiveCategory(key); setLiveSelectedId(null); setLiveSize(null); setLiveAgreed(false); }}
                  onSelect={(item) => { setLiveSelectedId(item.id); setLiveSize(null); setLiveAgreed(false); }}
                />
                <LiveGiftHistory view={live} firstName={identity.customer.firstName} />
              </div>
              <aside className="gift-right">
                <LiveGiftReview
                  view={live}
                  selected={liveSelected}
                  customerName={identity.customer.fullName}
                  firstName={firstName}
                  size={liveSize ?? (liveSelected?.sizes?.includes('L') ? 'L' : liveSelected?.sizes?.[0] ?? null)}
                  agreed={liveAgreed}
                  onSizeChange={setLiveSize}
                  onAgreementChange={setLiveAgreed}
                />
                <LiveGiftEligibility view={live} />
                <section className="gift-followup" data-component="gift-followup">
                  <span className="mini-avatar">{identity.owner.initials}</span>
                  <div>
                    <strong>{ownerFirst ? `${ownerFirst} owns the follow-up` : 'No HubSpot owner on this contact yet'}</strong>
                    <p>If the shipment changes or gift availability changes, review the attachment before fulfillment.</p>
                  </div>
                </section>
              </aside>
            </div>
          ) : (
          <div className="gift-grid">
            <div className="gift-left">
              <section className="card" data-testid="gifts-real-empty" style={{ padding: 24 }}>
                <div className="card-head">
                  <h3>Gifts on file</h3>
                  <span className="small">HubSpot contact</span>
                </div>
                <p><strong>Nothing on file yet for {firstName}.</strong></p>
                <p>
                  No previous gifts, pending gift or eligibility result is recorded for this contact in this
                  workspace. The sample catalog is not shown on a real contact.
                </p>
              </section>
            </div>
            <aside className="gift-right">
              <section className="card" data-component="gift-review" data-testid="gifts-real-controls">
                <div className="card-head">
                  <h3>Send a gift</h3>
                  <span className="small" id="gift-gate-note">Paused by the live-write gate</span>
                </div>
                <div className="gift-review">
                  <div className="gift-facts">
                    <div>
                      <span>Customer</span>
                      <strong>{identity.customer.fullName}</strong>
                    </div>
                    <div>
                      <span>Eligibility</span>
                      <strong>Not read yet</strong>
                    </div>
                  </div>
                  <button className="blue-action" type="button" disabled aria-describedby="gift-gate-note" title={LIVE_WRITE_PAUSED_LABEL}>Add to next shipment</button>
                  <button className="outlined" type="button" disabled aria-describedby="gift-gate-note" style={{ marginTop: 8 }} title={LIVE_WRITE_PAUSED_LABEL}>Ship now</button>
                  <p className="gift-review-note">
                    Gift requests are paused by the live-write gate. No shipment or order is created from this screen.
                  </p>
                </div>
              </section>
            </aside>
          </div>
          )}
        </DataBoundary>

        <UpNext customerId={customerId} />
        <ActionFooter
          disabled={true}
          noteWhenDisabled={live ? 'Gift review' : 'No gift history loaded for this contact'}
          noteWhenEnabled="Ready to continue"
          summaryBody={live ? 'Confirm the gift request or retain its next step before finishing.' : 'Note any gift request in HubSpot before finishing.'}
        />
      </WorkspaceChrome>
    );
  }

  return (
    <WorkspaceChrome screen="gifts" rail="my-work">
      <div className="breadcrumbs">
        <div>
          My work
          <span className="slash">/</span>
          <strong>{SARAH.fullName}</strong>
        </div>
        <span className="preview">Design preview &middot; Sample gifts, stock and eligibility</span>
      </div>
      <PriorityFocus
        ariaLabel="GIFTS · CUSTOMER APPRECIATION"
        eyebrow="GIFTS · CUSTOMER APPRECIATION"
        heading={`Choose a gift for ${SARAH.firstName}`}
        what={{
          label: 'WHAT',
          heading: `Choose ${SARAH.firstName}'s appreciation gift`,
          iconId: 'message',
        }}
        whyNow={{
          label: 'WHY NOW',
          body: `Review available gifts and confirm the right shipment for ${SARAH.firstName}.`,
          iconId: 'clock',
        }}
        nextAction={{
          label: 'NEXT ACTION',
          body: 'Choose the gift and size, then add to a shipment or ship now.',
          detail: 'Confirm the choice before attaching the gift.',
          iconId: 'target',
        }}
        ownership={{
          label: 'OWNERSHIP',
          iconId: 'people',
          detail: 'Primary account owner. Coverage preserves ownership.',
        }}
      />
      <IdentityRow customer={SARAH} badgeLabel="Active subscriber" />
      <FileShortcuts customerId={customerId} firstName={SARAH.firstName} />
      <CustomerTabs customerId={customerId} active="gifts" />

      <DataBoundary state={state} testId="gifts-boundary">
        <div className="gift-grid">
          <div className="gift-left">
            <GiftCatalog
              category={category}
              selectedId={selected.id}
              onCategoryChange={handleCategoryChange}
              onSelect={handleSelect}
            />
            <GiftDelivery
              mode={delivery}
              selectedShipmentId={selectedShipmentId}
              addressConfirmed={addressConfirmed}
              onModeChange={handleModeChange}
              onShipmentChange={(s) => {
                setSelectedShipmentId(s.id);
                resetSelection();
              }}
              onAddressConfirmChange={(c) => {
                setAddressConfirmed(c);
                resetSelection();
              }}
            />
            <GiftHistory items={[]} />
          </div>
          <aside className="gift-right">
            <GiftReview
              selected={selected}
              customerName={SARAH.fullName}
              size={size}
              shipmentSummary={shipmentSummary}
              variantSummary={variantSummary}
              shippingSummary={shippingSummary}
              agreementCopy={agreementCopy}
              agreed={agreed}
              attachLabel={attachLabel}
              reviewNote={reviewNote}
              resultMessage={resultMessage}
              isShirt={isShirt}
              canSubmit={canSubmit}
              onSizeChange={setSize}
              onAgreementChange={setAgreed}
              onAttach={handleAttach}
            />
            <GiftEligibility
              checks={[
                { label: 'Eligible in this sample', passing: true },
                { label: 'No pending gift shown', passing: true },
                { label: 'Active shipment available', passing: true },
              ]}
            />
            <GiftFollowup />
          </aside>
        </div>
      </DataBoundary>

      <UpNext customerId={customerId} />
      <ActionFooter
        disabled={true}
        noteWhenDisabled="Gift review"
        noteWhenEnabled="Ready to continue"
        summaryBody="Confirm the gift request or retain its next step before finishing."
      />
    </WorkspaceChrome>
  );
}

export default GiftsReviewScreen;
